/**
 * Lightweight SQL tokenizer for dashboard syntax highlighting.
 * Not a full parser — enough for keywords, strings, comments, and numbers.
 */

export type SqlTokenType =
  | "keyword"
  | "string"
  | "comment"
  | "number"
  | "ident"
  | "punct"
  | "ws";

export type SqlToken = {
  type: SqlTokenType;
  value: string;
};

/** Common SQL keywords (uppercased match; display preserves source case). */
const KEYWORDS = new Set(
  [
    "select",
    "from",
    "where",
    "and",
    "or",
    "not",
    "null",
    "is",
    "in",
    "like",
    "ilike",
    "between",
    "exists",
    "case",
    "when",
    "then",
    "else",
    "end",
    "as",
    "on",
    "join",
    "inner",
    "left",
    "right",
    "full",
    "outer",
    "cross",
    "using",
    "group",
    "by",
    "order",
    "asc",
    "desc",
    "limit",
    "offset",
    "having",
    "with",
    "recursive",
    "union",
    "all",
    "distinct",
    "insert",
    "into",
    "values",
    "update",
    "set",
    "delete",
    "create",
    "alter",
    "drop",
    "table",
    "view",
    "index",
    "unique",
    "primary",
    "key",
    "foreign",
    "references",
    "constraint",
    "default",
    "check",
    "cascade",
    "restrict",
    "if",
    "true",
    "false",
    "returning",
    "explain",
    "analyze",
    "begin",
    "commit",
    "rollback",
    "transaction",
    "grant",
    "revoke",
    "schema",
    "column",
    "add",
    "rename",
    "to",
    "type",
    "cast",
    "coalesce",
    "count",
    "sum",
    "avg",
    "min",
    "max",
    "over",
    "partition",
    "window",
    "lateral",
    "array",
    "json",
    "jsonb",
    "interval",
    "timestamp",
    "date",
    "time",
    "boolean",
    "integer",
    "text",
    "varchar",
    "serial",
    "bigserial",
    "uuid",
  ].map((k) => k.toUpperCase()),
);

function isIdentStart(ch: string): boolean {
  return /[A-Za-z_$]/.test(ch);
}

function isIdentPart(ch: string): boolean {
  return /[A-Za-z0-9_$]/.test(ch);
}

/**
 * Tokenize SQL source into highlight spans. Always covers the full string
 * so a mirrored textarea and highlight layer stay aligned.
 */
export function tokenizeSQL(source: string): SqlToken[] {
  const tokens: SqlToken[] = [];
  let i = 0;
  const n = source.length;

  while (i < n) {
    const ch = source[i]!;

    // Whitespace
    if (ch === " " || ch === "\t" || ch === "\n" || ch === "\r") {
      let j = i + 1;
      while (j < n) {
        const c = source[j]!;
        if (c !== " " && c !== "\t" && c !== "\n" && c !== "\r") break;
        j++;
      }
      tokens.push({ type: "ws", value: source.slice(i, j) });
      i = j;
      continue;
    }

    // Line comment --
    if (ch === "-" && source[i + 1] === "-") {
      let j = i + 2;
      while (j < n && source[j] !== "\n") j++;
      tokens.push({ type: "comment", value: source.slice(i, j) });
      i = j;
      continue;
    }

    // Block comment /* */
    if (ch === "/" && source[i + 1] === "*") {
      let j = i + 2;
      while (j < n && !(source[j] === "*" && source[j + 1] === "/")) j++;
      if (j < n) j += 2;
      tokens.push({ type: "comment", value: source.slice(i, j) });
      i = j;
      continue;
    }

    // Single-quoted string
    if (ch === "'") {
      let j = i + 1;
      while (j < n) {
        if (source[j] === "'") {
          if (source[j + 1] === "'") {
            j += 2;
            continue;
          }
          j++;
          break;
        }
        j++;
      }
      tokens.push({ type: "string", value: source.slice(i, j) });
      i = j;
      continue;
    }

    // Double-quoted identifier
    if (ch === '"') {
      let j = i + 1;
      while (j < n) {
        if (source[j] === '"') {
          if (source[j + 1] === '"') {
            j += 2;
            continue;
          }
          j++;
          break;
        }
        j++;
      }
      tokens.push({ type: "ident", value: source.slice(i, j) });
      i = j;
      continue;
    }

    // Number
    if (
      (ch >= "0" && ch <= "9") ||
      (ch === "." && source[i + 1] !== undefined && source[i + 1]! >= "0" && source[i + 1]! <= "9")
    ) {
      let j = i;
      if (ch === ".") j++;
      while (j < n && source[j]! >= "0" && source[j]! <= "9") j++;
      if (source[j] === "." && j === i) {
        // already handled leading dot
      } else if (source[j] === "." && j > i) {
        j++;
        while (j < n && source[j]! >= "0" && source[j]! <= "9") j++;
      }
      // scientific notation
      if (j < n && (source[j] === "e" || source[j] === "E")) {
        let k = j + 1;
        if (k < n && (source[k] === "+" || source[k] === "-")) k++;
        if (k < n && source[k]! >= "0" && source[k]! <= "9") {
          j = k;
          while (j < n && source[j]! >= "0" && source[j]! <= "9") j++;
        }
      }
      tokens.push({ type: "number", value: source.slice(i, j) });
      i = j;
      continue;
    }

    // Identifier / keyword
    if (isIdentStart(ch)) {
      let j = i + 1;
      while (j < n && isIdentPart(source[j]!)) j++;
      const value = source.slice(i, j);
      const type: SqlTokenType = KEYWORDS.has(value.toUpperCase())
        ? "keyword"
        : "ident";
      tokens.push({ type, value });
      i = j;
      continue;
    }

    // Punctuation / operators (single char; multi-char ops as successive punct)
    tokens.push({ type: "punct", value: ch });
    i++;
  }

  return tokens;
}

/** Tailwind/class names for token types on the dark code-block surface. */
export function sqlTokenClassName(type: SqlTokenType): string {
  switch (type) {
    case "keyword":
      return "text-[#7eb6ff]";
    case "string":
      return "text-[#ce9178]";
    case "comment":
      return "text-[#6a9955] italic";
    case "number":
      return "text-[#b5cea8]";
    case "ident":
      return "text-code-block-text";
    case "punct":
      return "text-[#d4d4d4]";
    case "ws":
    default:
      return "";
  }
}
